// Welcome + Resume cards for the Coach surface.
//
// Welcome (first-ever visit, zero sessions): editorial headline, body
// paragraph explaining the Notebook, and three starter buttons the user
// can tap to seed their first question. The Composer below the welcome
// stays visible so they can also just type.
//
// Resume (returning user who opens Coach without picking a session, has
// prior sessions): "Pick up where you left off" card showing the most
// recent session title + its coach-written summary, with Resume and Start-
// new buttons. Appears above an otherwise-empty thread body.

import { useI18n } from '../i18n/index.js';
import styles from './Coach.module.css';

export interface CoachWelcomeProps {
  /** User's active-framework-linked starter prompt, if any. V1: hidden
   *  (framework-coupling lookup lands with MR E). */
  frameworkStarter?: string;
  /** Called when the user clicks a starter. The parent should set the
   *  composer value + submit it as a new user message. */
  onStarter: (text: string) => void;
}

const DEFAULT_STARTERS: Array<{ text: string; textFr: string; small: string; smallFr: string }> = [
  {
    text: "I need to defend next year's legal budget",
    textFr: "Je dois défendre le budget juridique de l'an prochain",
    small: 'D5 · BUDGET & PERFORMANCE',
    smallFr: 'D5 · BUDGET & PERFORMANCE',
  },
  {
    text: 'Our contract review is a bottleneck. Where do I start?',
    textFr: 'Notre revue des contrats est un goulot. Par où commencer ?',
    small: 'D3 · LEGAL OPS & TECH',
    smallFr: 'D3 · OPS & TECH JURIDIQUES',
  },
  {
    text: 'How should I structure my team for the next phase?',
    textFr: 'Comment structurer mon équipe pour la phase suivante ?',
    small: 'D2 · TEAM & TALENT',
    smallFr: 'D2 · ÉQUIPE & TALENTS',
  },
];

export function CoachWelcome({ onStarter }: CoachWelcomeProps) {
  const { t, lang } = useI18n();
  return (
    <div className={styles.welc}>
      <div className={styles.welcLegal}>{t('coach.legal')}</div>
      <h2 className={styles.welcTitle}>{t('history.welcome.title')}</h2>
      <p className={styles.welcBody}>{t('history.welcome.body')}</p>
      {DEFAULT_STARTERS.map((s, i) => {
        const text = lang === 'FR' ? s.textFr : s.text;
        const small = lang === 'FR' ? s.smallFr : s.small;
        return (
          <button
            key={i}
            type="button"
            className={styles.starter}
            onClick={() => onStarter(text)}
          >
            {text}
            <small className={styles.starterSmall}>{small}</small>
          </button>
        );
      })}
    </div>
  );
}

export interface CoachResumeProps {
  title: string;
  summary: string | null;
  onResume: () => void;
  onStartNew: () => void;
}

export function CoachResume({ title, summary, onResume, onStartNew }: CoachResumeProps) {
  const { t } = useI18n();
  return (
    <div className={styles.resume}>
      <div className={styles.resumeEyebrow}>{t('history.resume.eyebrow')}</div>
      <div className={styles.resumeTitle}>{title}</div>
      {summary && <div className={styles.resumeSummary}>{summary}</div>}
      <div className={styles.resumeActions}>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnBrand}`}
          onClick={onResume}
        >
          {t('history.resume.go')}
        </button>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnGhost}`}
          onClick={onStartNew}
        >
          {t('history.resume.other')}
        </button>
      </div>
    </div>
  );
}
